import {t} from "./i18n/index.js";
// Geometri-writeback for integrationsbundne Features (Registreringsprofiler, trin H).
//
// Potree kender ingen integration. Når en bundet Features geometri ændres af brugeren (træk, indsæt/slet punkt, menuer,
// Topologi-tjek), og adapteren ikke har låst geometrien (`getEditingConstraints().geometryLocked === false`), venter
// køen til ændringen er faldet til ro og kalder adapterens `persistGeometry(...)` ÉN gang pr. afsluttet ændring:
//
//   persistGeometry({feature, binding, geometry, previousGeometry}) -> Promise<{ok: true} | {ok: false, message?}>
//
// `previousGeometry` er den sidst bekræftede (persisterede) geometri. Ved afvist/fejlet skrivning rulles den lokale
// geometri tilbage dertil (`restore`), så PointView aldrig viser en geometri, der ikke findes i kilden. En ændring under
// en igangværende skrivning afventer den og sendes bagefter (aldrig to samtidige skrivninger pr. Feature).
//
// Afhængigheder er injiceret (ingen THREE/DOM), så køen kan testes isoleret.

const clone = value => JSON.parse(JSON.stringify(value));

function sameCoordinates(a, b){
	return JSON.stringify(a) === JSON.stringify(b);
}

export class GeometryWritebackQueue{
	/**
	 * @param {object} deps
	 * @param {(id:string)=>object|null} deps.getFeature
	 * @param {(feature:object)=>{adapter:object, binding:object}|null} deps.resolve   integrationen for featuren
	 * @param {(feature:object)=>boolean} deps.isLocked                                 geometri-låst nu?
	 * @param {(id:string, geometry:object)=>boolean} deps.restore                     sæt lokal geometri (uden ny writeback)
	 * @param {(message:string)=>void} [deps.notify]
	 * @param {()=>boolean} [deps.isInteracting]                                         true mens brugeren trækker
	 * @param {number} [deps.delay]
	 */
	constructor(deps){
		this.deps = deps;
		this.delay = deps.delay != null ? deps.delay : 400;
		this.baselines = new Map(); // id -> sidst persisterede geometri
		this.state = new Map();     // id -> {timer, inflight}
		this.suppressed = 0;
	}

	// Kaldes FØR featurens koordinater overskrives: gem den hidtidige geometri som baseline (kun første gang).
	beforeChange(feature){
		if(this.suppressed || this.baselines.has(feature.id)) return;
		if(!this.deps.resolve(feature)) return;
		this.baselines.set(feature.id, clone(feature.geometry));
	}

	// Kaldes EFTER at ændringen er anvendt: planlæg en (afventende) skrivning.
	afterChange(feature){
		if(this.suppressed) return;
		const hit = this.deps.resolve(feature);
		if(!hit || typeof hit.adapter.persistGeometry !== "function" || this.deps.isLocked(feature)){
			this.baselines.delete(feature.id);
			return;
		}
		this._schedule(feature.id, this.delay);
	}

	// Host-drevet synkronisering/rollback: ændringer indeni er aldrig bruger-ændringer.
	runSuppressed(fn){
		this.suppressed++;
		try{
			return fn();
		}finally{
			this.suppressed--;
		}
	}

	// Kilden er sandheden (fx efter load): baselinen og en evt. afventende skrivning forkastes.
	reset(id){
		this.baselines.delete(id);
		const state = this.state.get(id);
		if(state && state.timer) clearTimeout(state.timer);
		this.state.delete(id);
	}

	dispose(){
		for(const state of this.state.values()) if(state.timer) clearTimeout(state.timer);
		this.state.clear();
		this.baselines.clear();
	}

	_schedule(id, delay){
		const state = this.state.get(id) || {timer: null, inflight: false};
		if(state.timer) clearTimeout(state.timer);
		state.timer = setTimeout(() => this._flush(id), delay);
		this.state.set(id, state);
	}

	_flush(id){
		const state = this.state.get(id);
		if(!state) return;
		state.timer = null;

		const feature = this.deps.getFeature(id);
		if(!feature){
			this.reset(id);
			return;
		}
		// Ikke midt i en træk-interaktion; og ikke to samtidige skrivninger pr. Feature.
		if(this.deps.isInteracting && this.deps.isInteracting()){
			this._schedule(id, 150);
			return;
		}
		if(state.inflight) return;

		const hit = this.deps.resolve(feature);
		const baseline = this.baselines.get(id);
		if(!hit || !baseline || typeof hit.adapter.persistGeometry !== "function" || this.deps.isLocked(feature)){
			this.baselines.delete(id);
			return;
		}

		const next = clone(feature.geometry);
		if(sameCoordinates(baseline.coordinates, next.coordinates)){
			this.baselines.delete(id);
			return;
		}

		state.inflight = true;
		Promise.resolve()
			.then(() => hit.adapter.persistGeometry({feature, binding: hit.binding, geometry: next, previousGeometry: clone(baseline)}))
			.catch((error) => {
				console.error("Integrations-adapter: persistGeometry fejlede", error);
				return {ok: false};
			})
			.then((result) => {
				state.inflight = false;
				const live = this.deps.getFeature(id);
				if(!live){
					this.reset(id);
					return;
				}

				if(result && result.ok === true){
					this.baselines.set(id, next);
					// Ændret igen under skrivningen: send den nyeste geometri; ellers er baselinen ikke længere nødvendig.
					if(sameCoordinates(next.coordinates, live.geometry.coordinates)) this.baselines.delete(id);
					else this._schedule(id, 0);
					return;
				}

				// Definitiv fejl (eller uvist udfald): lokal geometri rulles tilbage til den sidst bekræftede.
				this.baselines.delete(id);
				this.runSuppressed(() => this.deps.restore(id, baseline));
				const message = (result && result.message) || t("Geometrien kunne ikke gemmes. Den er sat tilbage.");
				if(this.deps.notify) this.deps.notify(message);
			});
	}
}
